import type { TooltipProps } from '@florencelabs/tooltip';
import type {
  FieldValues,
  Control,
  RegisterOptions,
  Path,
  UseFormWatch,
} from 'react-hook-form';

export enum FormBlockType {
  TEXT = 'text',
  CHECKBOX = 'checkbox',
  SELECT = 'select',
  TEXTAREA = 'textarea',
  RADIO = 'radio',
  COMBOBOX = 'combobox',
  DATE = 'date',
  TEXT_CONTENT = 'text-content',
  SECTION = 'section',
  ROW = 'row',
}

export type FormContentType =
  | FormBlockType.SECTION
  | FormBlockType.TEXT_CONTENT
  | FormBlockType.ROW;

export type FormFieldType = Exclude<FormBlockType, FormContentType>;

interface KeyedBlock<FormValues extends FieldValues> {
  name?: Path<FormValues>;
  blockId?: string;
}

interface BaseBlockConfig<FormValues extends FieldValues>
  extends KeyedBlock<FormValues> {
  type: FormBlockType;
  isHiddenValidation?: (formStatus: FormValues) => boolean;
}

interface TextFieldMaskConfig {
  pattern: string;
  placeholder?: string;
}

interface BaseField<FormValues extends FieldValues>
  extends BaseBlockConfig<FormValues> {
  type: FormFieldType;
  name: Path<FormValues>;
  label: string;
  validation?: RegisterOptions<FormValues>;
}
interface DateField<FormValues extends FieldValues>
  extends BaseField<FormValues> {
  type: FormBlockType.DATE;
  validations?: ((
    t: (token: string) => string,
  ) => (value: string) => string | boolean)[];
}

export interface Option {
  value: string;
  label: string;
}

interface SelectField<FormValues extends FieldValues>
  extends BaseField<FormValues> {
  type: FormBlockType.SELECT;
  options: Option[];
  tooltip?: string;
  tooltipVariant?: TooltipProps['variant'];
}

export interface ComboBoxField<FormValues extends FieldValues>
  extends BaseField<FormValues> {
  type: FormBlockType.COMBOBOX;
  options: Option[];
}

interface TextAreaField<FormValues extends FieldValues>
  extends BaseField<FormValues> {
  type: FormBlockType.TEXTAREA;
  placeholder?: string;
}

interface TextField<FormValues extends FieldValues>
  extends BaseField<FormValues> {
  type: FormBlockType.TEXT;
  placeholder?: string;
  mask?: TextFieldMaskConfig;
  inputType?: string;
  validations?: ((value: string) => boolean | string)[];
}

interface CheckboxField<FormValues extends FieldValues>
  extends BaseField<FormValues> {
  type: FormBlockType.CHECKBOX;
  ariaDescribedBy?: string;
}

interface RadioField<FormValues extends FieldValues>
  extends BaseField<FormValues> {
  type: FormBlockType.RADIO;
  options: Option[];
  onOptionSelected?: (option: Option) => void;
}

interface TextContentLink {
  text: string;
  href?: string;
  target?: '_blank' | '_self';
}

export interface FormTextContentConfig extends BaseBlockConfig<any> {
  type: FormBlockType.TEXT_CONTENT;
  blockId: string;
  content: string | TextContentLink | (TextContentLink | string)[];
  separator?: string;
}

export interface FormRowConfig<FormValues extends FieldValues>
  extends BaseBlockConfig<FormValues> {
  type: FormBlockType.ROW;
  blockId: string;
  children: (FormContentConfig<FormValues> | FormFieldConfig<FormValues>)[];
}

export interface FormSectionConfig<FormValues extends FieldValues>
  extends BaseBlockConfig<FormValues> {
  type: FormBlockType.SECTION;
  title?: string;
  blockId: string;
  variant?: 'default' | 'secondary';
  children: FormBlockConfig<FormValues>[];
}

export type FormContentConfig<FormValues extends FieldValues> =
  | FormTextContentConfig
  | FormSectionConfig<FormValues>
  | FormRowConfig<FormValues>;

export type FormFieldConfig<FormValues extends FieldValues> =
  | TextField<FormValues>
  | CheckboxField<FormValues>
  | SelectField<FormValues>
  | TextAreaField<FormValues>
  | RadioField<FormValues>
  | DateField<FormValues>
  | ComboBoxField<FormValues>;

export type FormBlockConfig<FormValues extends FieldValues> =
  | FormContentConfig<FormValues>
  | FormFieldConfig<FormValues>;

export type FormConfig<FormValues extends FieldValues> =
  FormBlockConfig<FormValues>[];

export const isFieldBlock = <FormValues extends FieldValues>(
  block: FormBlockConfig<FormValues>,
): block is FormFieldConfig<FormValues> =>
  ![
    FormBlockType.SECTION,
    FormBlockType.TEXT_CONTENT,
    FormBlockType.ROW,
  ].includes(block.type);

export interface HookFormProps<FormValues extends FieldValues> {
  control: Control<FormValues>;
  watch?: UseFormWatch<FormValues>;
}
